import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    FormHelperText,
    CircularProgress,
    TextField,
    InputAdornment,
    ListSubheader
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AdminApi from '../../../../Service/Admin.api';
import { SingleSkillDropdownProps } from '../../types/profileForm.types';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const SingleSkillDropdown: React.FC<SingleSkillDropdownProps> = ({
    id,
    label,
    value,
    error,
    required = false,
    onChange,
    onBlur,
    excludeSkills = [],
}) => {
    const [skills, setSkills] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const response = await AdminApi.getSkills();
                if (response && response.data) {
                    setSkills(response.data);
                }
            } catch (error) {
                console.error("Error fetching skills:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchSkills();
    }, []);

    const isCategoryItem = (skill: any) => {
        return skill.isCategory || /^\d+\)/.test(skill.label || "");
    };

    const getProcessedSkills = React.useMemo(() => {
        // Filter out excluded skills first
        const available = skills.filter(s => !excludeSkills.includes(s.label));
        
        if (!searchTerm) {
            return available.map(s => ({ ...s, isCategory: isCategoryItem(s) }));
        }

        const query = searchTerm.toLowerCase().trim();
        const result: any[] = [];
        let currentCategory: any = null;
        let categoryHasMatch = false;
        let categoryItems: any[] = [];

        available.forEach((skill) => {
            const isCat = isCategoryItem(skill);
            
            if (isCat) {
                if (currentCategory && categoryHasMatch) {
                    result.push({ ...currentCategory, isCategory: true });
                    result.push(...categoryItems);
                }
                currentCategory = skill;
                categoryHasMatch = skill.label.toLowerCase().includes(query);
                categoryItems = [];
            } else {
                const isMatch = skill.label.toLowerCase().includes(query);
                if (isMatch) {
                    categoryHasMatch = true;
                    categoryItems.push(skill);
                }
            }
        });

        if (currentCategory && categoryHasMatch) {
            result.push({ ...currentCategory, isCategory: true });
            result.push(...categoryItems);
        }

        if (result.length === 0 && available.length > 0) {
            return available.filter(s => !isCategoryItem(s) && s.label.toLowerCase().includes(query));
        }

        return result;
    }, [searchTerm, skills, excludeSkills]);

    const handleChange = (event: SelectChangeEvent<string>) => {
        const selectedValue = event.target.value;
        onChange('skillName', selectedValue);
        
        const skill = skills.find(s => s.label === selectedValue);
        if (skill) {
            onChange('PkSkillDetails', skill.id);
        }
    };

    const handleBlur = () => {
        onBlur('skillName');
    };

    // No longer need separate mappers, handled in getProcessedSkills

    return (
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            <Typography
                variant="subtitle1"
                sx={{
                    minWidth: { xs: '120px', sm: '200px' },
                    flexShrink: 0,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#212529',
                }}
            >
                {label}
                {required && (
                    <Typography component="span" sx={{ color: '#dc3545', ml: 0.5 }}>
                        *
                    </Typography>
                )}
            </Typography>

            <Box sx={{ flex: 1 }}>
                <FormControl fullWidth error={!!error}>
                    <Select
                        value={value || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        displayEmpty
                        MenuProps={{
                            ...MenuProps,
                            autoFocus: false, // Prevent focus jump to first item
                        }}
                        sx={{
                            height: '48px',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: error ? '#dc3545' : '#dee2e6',
                                borderWidth: '1px',
                            },
                        }}
                    >
                        {/* Search Bar */}
                        <ListSubheader
                            sx={{
                                backgroundColor: '#fff',
                                p: 1,
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <TextField
                                size="small"
                                autoFocus
                                placeholder="Search skills..."
                                fullWidth
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Escape') {
                                        // Prevents select from closing
                                        e.stopPropagation();
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </ListSubheader>

                        <MenuItem value="" disabled>
                            <Typography sx={{ color: '#6c757d', fontSize: '14px' }}>
                                {isLoading ? 'Loading...' : 'Select a skill...'}
                            </Typography>
                        </MenuItem>
                        {isLoading ? (
                            <MenuItem disabled sx={{ justifyContent: 'center' }}>
                                <CircularProgress size={20} />
                            </MenuItem>
                        ) : getProcessedSkills.length === 0 ? (
                            <MenuItem disabled sx={{ py: 2, justifyContent: 'center' }}>
                                <Typography color="textSecondary" sx={{ fontSize: '14px' }}>
                                    No results found
                                </Typography>
                            </MenuItem>
                        ) : (
                            getProcessedSkills.map((skill) => {
                                if (skill.isCategory) {
                                    return (
                                        <MenuItem
                                            disabled
                                            key={`cat-${skill.id}`}
                                            sx={{
                                                backgroundColor: '#f8f9fa !important',
                                                fontWeight: '800 !important',
                                                color: '#1a237e !important',
                                                fontSize: '12px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px',
                                                pt: 2,
                                                pb: 1,
                                                opacity: '1 !important',
                                                cursor: 'default'
                                            }}
                                        >
                                            {skill.label}
                                        </MenuItem>
                                    );
                                }
                                return (
                                    <MenuItem
                                        key={skill.id}
                                        value={skill.label}
                                        sx={{
                                            fontSize: '14px',
                                            pl: 4, // Intention Right for items
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: '#f0f4f8'
                                            }
                                        }}
                                    >
                                        {skill.label}
                                    </MenuItem>
                                );
                            })
                        )}
                    </Select>
                    {error && (
                        <FormHelperText sx={{ fontSize: '12px', color: '#dc3545' }}>
                            {error}
                        </FormHelperText>
                    )}
                </FormControl>
            </Box>
        </Box>
    );
};

export default SingleSkillDropdown;